import {
  APP_MODULE_DESCRIPTIONS,
  APP_MODULE_HIDES,
  APP_MODULE_LABELS,
  type AppModuleCatalogItem,
  type AppModuleCode,
} from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  filterModuleItems,
  formatModuleLabels,
  groupModuleItems,
  moduleLabel,
} from '@/products/helpdesk/features/settings/system-modules-catalog';
import {
  usePatchSystemModules,
  useSystemModulesCatalog,
} from '@/products/helpdesk/features/settings/use-system-modules';
import { ApiRequestError } from '@/lib/api';

const IN_FLIGHT_MESSAGE =
  'Bereits laufende Vorgänge (Freigaben, Umfragen, SLA-Uhren, Remote-Sitzungen) können zu Ende laufen. Neue werden nicht gestartet. Daten bleiben erhalten.';

export function SystemModulesPage() {
  const catalog = useSystemModulesCatalog();
  const patch = usePatchSystemModules();
  const [query, setQuery] = useState('');
  const [pendingOff, setPendingOff] = useState<AppModuleCatalogItem | null>(null);

  const sections = useMemo(
    () => groupModuleItems(filterModuleItems(catalog.data?.items ?? [], query)),
    [catalog.data?.items, query],
  );

  async function applyToggle(code: AppModuleCode, enabled: boolean) {
    try {
      const result = await patch.mutateAsync({ code, enabled });
      const names = formatModuleLabels(result.changed.length > 0 ? result.changed : [code]);
      toast.success(
        enabled
          ? `${names} ${result.changed.length > 1 ? 'sind' : 'ist'} eingeschaltet.`
          : `${names} ${result.changed.length > 1 ? 'wurden' : 'wurde'} ausgeschaltet.`,
      );
      setPendingOff(null);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Das Modul konnte nicht geändert werden.',
      );
    }
  }

  function onCheckedChange(item: AppModuleCatalogItem, enabled: boolean) {
    if (enabled) {
      void applyToggle(item.code, true);
      return;
    }
    setPendingOff(item);
  }

  const pendingDependents = pendingOff
    ? pendingOff.dependents.filter((code) =>
        (catalog.data?.items ?? []).some((item) => item.code === code && item.enabled),
      )
    : [];

  return (
    <section className="mx-auto max-w-3xl space-y-8">
      <div className="space-y-3">
        <div>
          <h1 className="text-2xl font-bold">Systemmodule</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Ausgeschaltete Bereiche verschwinden in Navigation, Tickets und API. Daten bleiben
            erhalten.
          </p>
        </div>
        <Input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Modul suchen …"
          aria-label="Module durchsuchen"
        />
      </div>

      {catalog.isPending && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Katalog wird geladen …
        </p>
      )}

      {catalog.isError && (
        <p className="text-sm text-muted-foreground">
          {catalog.error instanceof ApiRequestError
            ? catalog.error.message
            : 'Der Modulkatalog konnte nicht geladen werden.'}
        </p>
      )}

      {catalog.data && sections.length === 0 && (
        <p className="text-sm text-muted-foreground">Keine Module passen zur Suche.</p>
      )}

      {sections.map((section) => (
        <section
          key={section.group}
          className="space-y-3"
          aria-labelledby={`module-group-${section.group}`}
        >
          <h2 id={`module-group-${section.group}`} className="text-lg font-semibold">
            {section.label}
          </h2>
          <div className="grid gap-4">
            {section.items.map((item) => {
              const titleId = `module-${item.code}-title`;
              const busy = patch.isPending && patch.variables?.code === item.code;
              return (
                <Card key={item.code}>
                  <CardHeader>
                    <CardTitle>
                      <h3 id={titleId}>{APP_MODULE_LABELS[item.code]}</h3>
                    </CardTitle>
                    <CardDescription>{APP_MODULE_DESCRIPTIONS[item.code]}</CardDescription>
                    <CardAction>
                      <Switch
                        checked={item.enabled}
                        disabled={busy}
                        aria-labelledby={titleId}
                        onCheckedChange={(checked) => onCheckedChange(item, checked)}
                      />
                    </CardAction>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-xs text-muted-foreground">Wird ausgeblendet:</span>
                      {APP_MODULE_HIDES[item.code].map((chip) => (
                        <Badge key={chip} variant="secondary">
                          {chip}
                        </Badge>
                      ))}
                    </div>
                    {item.dependents.length > 0 && (
                      <p className="text-sm text-muted-foreground">
                        Zieht mit: {item.dependents.map((code) => moduleLabel(code)).join(', ')}
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>
      ))}

      <AlertDialog
        open={pendingOff !== null}
        onOpenChange={(open) => {
          if (!open && !patch.isPending) {
            setPendingOff(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingOff ? `${moduleLabel(pendingOff.code)} ausschalten?` : 'Modul ausschalten?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDependents.length > 0
                ? `Wird ebenfalls ausgeschaltet: ${formatModuleLabels(pendingDependents)}. ${IN_FLIGHT_MESSAGE}`
                : IN_FLIGHT_MESSAGE}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                const item = pendingOff;
                if (!item) {
                  return;
                }
                void applyToggle(item.code, false);
              }}
            >
              Ausschalten
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
